import type { RouteConfig } from '@lit-labs/router';

// RouteConfig is a PathRouteConfig | URLPatternRouteConfig union — only the former has
// `path`. Every route in allRoutes (routes.ts) is path-based (none uses the `pattern:`
// form), but the type doesn't know that, so a plain `route.path` access doesn't compile
// under strict mode.
export const routePath = (route: RouteConfig): string | undefined =>
    'path' in route ? route.path : undefined;

/**
 * Pure, directly testable: applies the feature-flag gates to a route list.
 *
 * Deliberately kept in its own module, separate from routes.ts: routes.ts's `allRoutes`
 * pulls in the app's full DI/component graph (tsyringe container, repositories, app.state,
 * ...) at import time, which drags in browser-only globals (window, location, ...) that
 * don't exist in vitest's node test environment without an escalating chain of shims. This
 * file has none of that — it only depends on the RouteConfig type — so it can be unit
 * tested by importing it directly, with a fake route list, no shims needed.
 */
export const buildRoutes = (
    myViewEnabled: boolean,
    tenantThemeEnabled: boolean,
    allRoutes: RouteConfig[]
): RouteConfig[] => {
    // Gate the unfinished "Meine Ansicht" (/by-me) behind a build-time flag. When the
    // flag is off, the route is omitted so navigating to /by-me falls through to the
    // fallback (<ha-not-found>) instead of rendering <ha-user-view>.
    const withMyViewGate = myViewEnabled
        ? allRoutes
        : allRoutes.filter(route => routePath(route) !== '/by-me');

    // The tenant theme editor (feature plan 0054), behind its per-tenant runtime flag — same
    // fallthrough-to-not-found behaviour. No default value on purpose: a forgotten argument must
    // not leave the route open (fail-closed).
    return tenantThemeEnabled
        ? withMyViewGate
        : withMyViewGate.filter(route => routePath(route) !== '/settings/theme');
};
